import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentDetailsData, DefectDetailsSection } from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";
import { feetToInches } from "@/utils/mappings";
import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "defectDetails";

type DefectDetailsProps = {
  savedDefectDetails: DefectDetailsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setDefectDetails: (payload: {
    data: {
      defects: Array<{
        defect_length_total: string | number | null;
        unit_for_defect_length: string;
        defect_length_wrap: string | number | null;
        unit_for_defect_length_wrap: string;
      }>;
    };
    status: ExpandStatus;
  }) => void;
  componentDetailsData: ComponentDetailsData;
};
export function DefectDetails({
  toggleExpandableSection,
  savedDefectDetails,
  setDefectDetails,
  componentDetailsData,
}: DefectDetailsProps) {
  const componentDetails = componentDetailsData.components_data;

  const defectDetailsBaseSchema = z
    .object({
      defect_length_total: z.union([
        z.number().lt(2147483647),
        z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
      ]),
      unit_for_defect_length: z.string(),
      defect_length_wrap: z.union([
        z.number().lt(2147483647).gt(0),
        z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
      ]),
      unit_for_defect_length_wrap: z.string(),
    })
    .refine(
      (data) => {
        const isSameUnit = data.unit_for_defect_length === data.unit_for_defect_length_wrap;
        if (isSameUnit) {
          return Number(data.defect_length_wrap) >= Number(data.defect_length_total);
        } else {
          if (data.unit_for_defect_length === "Inches") {
            return feetToInches(data.defect_length_wrap) >= Number(data.defect_length_total);
          } else {
            return Number(data.defect_length_wrap) >= feetToInches(data.defect_length_total);
          }
        }
      },
      {
        message: "How much do you wish to wrap must be greater or equal to total defect length",
        path: ["defect_length_wrap"],
      }
    );

  const defectDetailsSchema = z.object({
    defects: z.array(defectDetailsBaseSchema),
  });
  type DefectDetailsSchema = z.infer<typeof defectDetailsSchema>;

  type SavedDefect = {
    defect_length_total: string | number | null;
    unit_for_defect_length: string;
    defect_length_wrap: string | number | null;
    unit_for_defect_length_wrap: string;
  };

  const initialValues =
    savedDefectDetails.isDataFilled && "defects" in savedDefectDetails.data
      ? savedDefectDetails.data.defects.map((v: SavedDefect) => {
          return {
            ...v,
            defect_length_wrap: v.defect_length_wrap ?? "",
            defect_length_total: v.defect_length_total ?? "",
          };
        })
      : componentDetails.map(() => {
          return {
            defect_length_total: "",
            unit_for_defect_length: "Inches",
            defect_length_wrap: "",
            unit_for_defect_length_wrap: "Inches",
          };
        });

  const form = useForm<DefectDetailsSchema>({
    resolver: zodResolver(defectDetailsSchema),
    defaultValues: {
      defects: initialValues,
    },
    values: {
      defects: initialValues,
    },
  });

  const { measureUnitOptions } = useConfigOptions();

  function handleSave(payload: DefectDetailsSchema) {
    setDefectDetails({
      data: payload,
      status: "done",
    });
  }

  const fieldWidth = "w-full sm:w-[270px]";

  return (
    <ExpandableSection
      status={savedDefectDetails.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Defect Details"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="flex flex-col justify-between gap-4">
          {componentDetails?.map((component, index) => {
            let title = "";
            if (!component.is_flanges && component.component_nps !== "custom") {
              title = `${component.component_nps} NPS ${component.component_schedule}`;
            } else if (component.is_flanges) {
              title = `Custom  ${component.component_diameter} ${component.unit_for_component_diameter} OD`;
            } else {
              title = `Custom  ${component.component_diameter} ${component.unit_for_component_diameter} OD X ${component.component_thickness} ${component.unit_for_component_thickness} THK`;
            }
            return (
              <div
                key={index}
                className={cn(
                  "flex-1 gap-5",
                  index !== componentDetails.length - 1 && "border-b border-black border-solid pb-4"
                )}
              >
                <Typography variant="text-md" weight="semibold" className="mb-2">
                  Component {index + 1}: {title}
                </Typography>
                <div className="basis-full" />
                <div className="mb-4 flex flex-wrap gap-2">
                  <FormField
                    control={form.control}
                    name={`defects.${index}.defect_length_total`}
                    render={({ field }) => (
                      <FormItem className={cn("flex flex-col", fieldWidth)}>
                        <FormLabel className="text-left">What is the total defect length?</FormLabel>
                        <FormControl>
                          <TextInput
                            placeholder="What is the total defect length?"
                            {...field}
                            step={inputStep}
                            type="number"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name={`defects.${index}.unit_for_defect_length`}
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <FormItem className={cn("flex flex-col", fieldWidth)}>
                          <FormLabel>Unit</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue="Inches" value={field.value}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue defaultValue={field.value} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent ref={field.ref}>
                              {measureUnitOptions.map((option) => {
                                return (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.title}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      );
                    }}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <FormField
                    control={form.control}
                    name={`defects.${index}.defect_length_wrap`}
                    render={({ field }) => (
                      <FormItem className={cn("flex flex-col", fieldWidth)}>
                        <FormLabel className="text-left">How much length do you wish to wrap?</FormLabel>
                        <FormControl>
                          <TextInput
                            placeholder="How much length do you wish to wrap?"
                            {...field}
                            step={inputStep}
                            type="number"
                            min={1}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name={`defects.${index}.unit_for_defect_length_wrap`}
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className={cn("flex flex-col", fieldWidth)}>
                        <FormLabel>Unit</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue="Inches" value={field.value as string}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent ref={field.ref}>
                            {measureUnitOptions.map((option) => {
                              return (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.title}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            );
          })}
          <Button className="ml-auto self-end" type="submit">
            Save
          </Button>
        </form>
      </Form>
    </ExpandableSection>
  );
}
